import { useEffect, useRef } from 'react';
import { useUnit } from 'effector-react';
import { useProductParams } from '../../../../hooks/useProductParams';
import { useTranslation } from 'react-i18next';

import {
  $cartState,
  SelectedProduct,
  handleSelectProduct,
  handleSetProductCount,
  handleAddProductInCart,
  handleSelectProductSize,
  handleUpdateCartItemCount,
  handleDecreaseProductCount,
  handleIncreaseProductCount,
} from '../../../../store/models/cart';
import Button from '../../../forms/Button/Button';
import GuaranteedSection from './GuaranteedSection';
import Counter from '../../../forms/Counter/Counter';
import useToaster from '../../../../hooks/useToaster';
import { $productsState } from '../../../../store/models/products';
import {
  EProducts,
  EProductSizes,
  EGrassFrequency,
} from '../../../../model/enums';
import { hasFrequencyConflict, persistCartFromStore } from '../../../../helpers/cart';
import { trackAddToCart, trackViewItem } from '../../../../analytics/ecommerce';

import styles from './styles.module.scss';

const ProductItemPayment = () => {
  const { t } = useTranslation();
  const {
    selectedProduct,
    selectedProductFrequency,
    selectedProductSize,
    selectedProductCount,
  } = useUnit($cartState);
  const { productType, productSize } = useProductParams();
  const { successMessage, errorMessage } = useToaster();
  const { accessories, catnips } = useUnit($productsState);

  const freqTextObj = {
    oneTime: t('oneTimePurchase'),
    week_price: t('payWeekly'),
    two_week_price: `${t('payEvery2Weeks')} (${t('popular')})`,
    three_week_price: t('payEvery3Weeks'),
    four_week_price: t('payEvery4Weeks'),
  };

  function addProductToCart() {
    if (!selectedProduct || !selectedProductSize) {
      console.error('Missing required fields to add product to cart.');
      errorMessage('Missing required fields to add product to cart.');
      return;
    }

    const prod = {
      productType: productType,
      size: selectedProductSize as EProductSizes,
      data: selectedProduct,
      frequency:
        productType === EProducts.grs || productType === EProducts.sds
          ? selectedProductFrequency
          : 'one_off_price',
      count: selectedProductCount,
    };

    // Read the cart from the Effector store (source of truth), not localStorage,
    // so we never operate on a stale copy and never write back the wrong index.
    const cart = $cartState.getState().cartState;

    // Keep the cart to a single subscription cadence so it stays a valid single
    // Stripe Checkout session. One-time additions are always allowed.
    if (hasFrequencyConflict(cart, prod.frequency)) {
      errorMessage(
        'Your cart already has a different subscription frequency. Finish that checkout first or change the existing item.',
      );
      return;
    }

    const existingProductIndex = cart.findIndex(
      (item: SelectedProduct) =>
        item.size === prod.size &&
        item.frequency === prod.frequency &&
        JSON.stringify(item.data) === JSON.stringify(prod.data),
    );

    if (existingProductIndex !== -1) {
      handleUpdateCartItemCount({
        index: existingProductIndex,
        count: cart[existingProductIndex].count + prod.count,
      });
    } else {
      // @ts-ignore
      handleAddProductInCart(prod);
    }

    persistCartFromStore();
    // @ts-ignore -- same shape the cart store accepts above
    trackAddToCart([prod]);

    successMessage('Product added to cart!');
  }

  // view_item once per product/size shown, as soon as its real data is loaded.
  const viewedKey = useRef<string | null>(null);
  useEffect(() => {
    if (!productType || !productSize || !selectedProduct) return;
    if (selectedProduct.size !== productSize) return; // store still holds the previous product
    const key = `${productType}/${productSize}`;
    if (viewedKey.current === key) return;
    viewedKey.current = key;
    trackViewItem({
      productType: productType as EProducts,
      size: productSize as EProductSizes,
      data: selectedProduct,
      frequency: (productType === EProducts.grs || productType === EProducts.sds
        ? selectedProductFrequency || EGrassFrequency.once
        : EGrassFrequency.once) as SelectedProduct['frequency'],
      count: 1,
    });
  }, [productType, productSize, selectedProduct, selectedProductFrequency]);

  useEffect(() => {
    if (productType) {
      handleSetProductCount(productType === EProducts.ctn ? 20 : 1);
    }
  }, [productType]);

  useEffect(() => {
    if (productType === EProducts.acs && productSize) {
      const accessoryItem = accessories.find(
        (item) => item.size === productSize,
      );

      if (accessoryItem) {
        // @ts-ignore
        handleSelectProduct(accessoryItem);
        handleSelectProductSize(productSize as EProductSizes);
      }
    }
    if (productType === EProducts.ctn && productSize) {
      const catnapItem = catnips.find((item) => item.size === productSize);

      if (catnapItem) {
        // @ts-ignore
        handleSelectProduct(catnapItem);
        handleSelectProductSize(productSize as EProductSizes);
      }
    }
  }, [productType, productSize, accessories, catnips]);

  return (
    <div className={styles.productItemPayment}>
      <div className={styles.priceWrapper}>
        <div className={styles.price}>
          €
          {selectedProductFrequency === EGrassFrequency.once
            ? selectedProduct?.price
            : selectedProduct?.subscribe_price}
        </div>
        {productType === EProducts.grs && (
          <p className={styles.priceTypeInfo}>
            {freqTextObj[selectedProductFrequency as keyof typeof freqTextObj]}
          </p>
        )}
      </div>
      <div className={styles.paymentControlBar}>
        <div className={styles.counterLabel}>{t('quantity')}</div>
        <div className={styles.btnsWrapper}>
          <Counter
            label=""
            value={selectedProductCount}
            handleDecrease={() => handleDecreaseProductCount(20)}
            handleIncrease={handleIncreaseProductCount}
            disabledDecrease={
              productType === EProducts.ctn ? selectedProductCount <= 20 : false
            }
          />
          <div className={styles.paymentBtnsWrapper}>
            <Button
              style={{
                height: '44px',
                padding: '0 20px',
                width: '100%',
              }}
              onClick={addProductToCart}
            >
              {t('addToCart')}
            </Button>
          </div>
        </div>
      </div>
      <GuaranteedSection />
    </div>
  );
};

export default ProductItemPayment;
